import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { SocketService } from 'app/pages/productos/budget/services/socket.service';
import { Observable, Subscription } from 'rxjs';

import {
  Meeting,
  MeetingRecurrencePattern,
} from 'app/models/meetings/meeting.models';
import {
  NotificationsService,
  Subnotification,
} from 'app/services/notifications/notifications.service';
import {
  AvatarAssistantService,
  AvatarCommand,
  AvatarMotionPreset,
  AvatarPackState,
  AvatarPreferences,
  AvatarQuickAction,
  ParsedRecurringReminder,
} from './avatar-assistant.service';

type AvatarReaction =
  | 'idle'
  | 'happy'
  | 'alert'
  | 'thinking'
  | 'sleeping'
  | 'wave';
type AvatarEmotion =
  | 'neutral'
  | 'happy'
  | 'worried'
  | 'tired'
  | 'focused'
  | 'celebrating';

type PanelMode = 'home' | 'agenda' | 'notifications' | 'reminder';

interface ReminderDraft {
  date: string;
  time: string;
  subject: string;
  description: string;
  reminderMinutes: number;
}

type ReminderRecurrenceMode = 'none' | 'daily' | 'weekly' | 'monthly';

interface QuickActionView {
  command: AvatarQuickAction;
  icon: string;
  label: string;
  primary?: boolean;
}

@Component({
  standalone: false,
  selector: 'app-avatar-assistant',
  templateUrl: './avatar-assistant.component.html',
  styleUrls: ['./avatar-assistant.component.css'],
})
export class AvatarAssistantComponent implements OnInit, OnDestroy {
  prefs!: AvatarPreferences;
  position = { x: 24, y: 120 };

  open = false;
  busy = false;
  mode: PanelMode = 'home';
  reaction: AvatarReaction = 'idle';
  emotion: AvatarEmotion = 'neutral';
  activeMotionPreset: AvatarMotionPreset = 'none';
  activePackUrl: string | null = null;

  commandInput = '';
  panelTitle = 'Asistente ZIVO';
  panelMessage = '¿Qué necesitas hacer?';
  speech = '';
  suggestedCommand: AvatarQuickAction | null = null;
  activeMeetingAlert: Meeting | null = null;

  meetings: Meeting[] = [];
  notifications: Subnotification[] = [];

  reminderStep = 1;
  reminderDraft: ReminderDraft = this.createReminderDraft();
  reminderRecurrenceMode: ReminderRecurrenceMode = 'none';
  reminderRecurrenceDay:
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY'
    | null = null;
  reminderError = '';
  reminderActionKey = '';

  readonly quickActionOptions: QuickActionView[] = [
    { command: 'TODAY', icon: 'today', label: 'Hoy' },
    { command: 'TOMORROW', icon: 'wb_sunny', label: 'Mañana' },
    { command: 'THIS_WEEK', icon: 'date_range', label: 'Semana' },
    {
      command: 'CREATE_REMINDER',
      icon: 'add_alert',
      label: 'Recordarme',
      primary: true,
    },
    {
      command: 'NEXT_MEETING',
      icon: 'event_upcoming',
      label: 'Próxima reunión',
    },
    { command: 'LIST_REMINDERS', icon: 'sticky_note_2', label: 'Notas' },
    { command: 'NOTIFICATIONS', icon: 'notifications', label: 'Novedades' },
    { command: 'OPEN_CALENDAR', icon: 'calendar_month', label: 'Calendario' },
  ];

  private dragging = false;
  private dragged = false;
  private dragOffset = { x: 0, y: 0 };
  private reactionTimer: number | null = null;
  private motionTimer: number | null = null;
  private speechTimer: number | null = null;
  private randomInteractionTimer: number | null = null;
  private roamTimer: number | null = null;
  roaming = false;
  private microsoftMeetingInitialized = false;
  private knownMicrosoftMeetingKeys = new Set<string>();
  private meetingWatchTimer: number | null = null;
  private dailyBriefTimer: number | null = null;
  private meetingCheckRunning = false;
  private notificationInitialized = false;
  private knownNotificationIds = new Set<number>();
  private alertedMeetingKeys = new Set<string>();
  private readonly packStates = new Set<AvatarPackState>();
  private briefCheckRunning = false;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly assistant: AvatarAssistantService,
    private readonly notificationsService: NotificationsService,
    private readonly socketService: SocketService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.assistant.refreshForCurrentUser();
    this.prefs = this.assistant.getSnapshot();
    this.position = this.initialPosition(this.prefs);
    this.activeMotionPreset = this.prefs.movementProfile.idle;

    this.subscriptions.add(
      this.assistant.preferences$.subscribe((preferences) => {
        const previous = this.prefs;
        this.prefs = preferences;
        if (this.reaction === 'idle' && !this.motionTimer) {
          this.activeMotionPreset = preferences.movementProfile.idle;
        }

        if (
          preferences.position &&
          (!previous ||
            previous.position?.x !== preferences.position.x ||
            previous.position?.y !== preferences.position.y)
        ) {
          this.position = this.clampPosition(preferences.position);
        }

        this.scheduleRandomInteraction();
        this.scheduleRoam();
        this.scheduleMeetingWatch();
        if (preferences.enabled) {
          this.scheduleDailyBriefs(400);
        }
      }),
    );

    this.subscriptions.add(
      this.notificationsService.getPreviewStream().subscribe((rows) => {
        this.handleNotificationStream(rows || []);
      }),
    );

    this.notificationsService.loadPreview('no_leidas');

    // Reuse the existing connection; only a refresh hint comes over Socket.IO.
    this.subscriptions.add(
      this.socketService.onAvatarCalendarChanged().subscribe(() => {
        if (!this.prefs?.enabled || !this.prefs.meetingAlertsEnabled) return;
        this.scheduleMeetingWatch(900);
      }),
    );
    this.loadMascotPack();
    this.scheduleRandomInteraction();
    this.scheduleRoam(4500);
    this.scheduleMeetingWatch();
    this.scheduleDailyBriefs(1600);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.clearTimer(this.reactionTimer);
    this.clearTimer(this.motionTimer);
    this.clearTimer(this.speechTimer);
    this.clearTimer(this.randomInteractionTimer);
    this.clearTimer(this.roamTimer);
    this.clearTimer(this.meetingWatchTimer);
    this.clearTimer(this.dailyBriefTimer);
  }

  get panelBelow(): boolean {
    return this.position.y < 360;
  }

  get isCustomAvatar(): boolean {
    return (
      this.prefs.appearance === 'custom' &&
      !!this.prefs.customAvatarDataUrl
    );
  }

  get showAvatar(): boolean {
    return !!this.prefs?.enabled;
  }

  get effectiveMotionPreset(): AvatarMotionPreset {
    return this.prefs.movement === 'quiet' ? 'none' : this.activeMotionPreset;
  }

  get motionIntensityPx(): number {
    const base = 2 + this.prefs.movementProfile.intensity * 1.25;
    return this.prefs.movement === 'soft' ? base * 0.68 : base;
  }

  get motionDurationSeconds(): number {
    const speed = Math.max(1, Math.min(10, this.prefs.movementProfile.speed));
    const base = Math.max(0.45, 3.8 - speed * 0.28);
    return this.prefs.movement === 'soft' ? base * 1.2 : base;
  }

  get showMusicalNotes(): boolean {
    return (
      this.prefs.movementProfile.effectsEnabled &&
      this.prefs.movementProfile.musicalNotesEnabled &&
      this.effectiveMotionPreset === 'sing'
    );
  }

  get showConfetti(): boolean {
    return (
      this.prefs.movementProfile.effectsEnabled &&
      this.prefs.movementProfile.confettiEnabled &&
      this.effectiveMotionPreset === 'celebrate'
    );
  }

  get showSleepEffect(): boolean {
    return (
      this.prefs.movementProfile.effectsEnabled &&
      this.effectiveMotionPreset === 'sleep'
    );
  }

  get showThinkEffect(): boolean {
    return (
      this.prefs.movementProfile.effectsEnabled &&
      this.effectiveMotionPreset === 'think'
    );
  }

  get showPackAnimation(): boolean {
    return (
      this.prefs.mascotStyle.animationPackEnabled &&
      Boolean(this.activePackUrl)
    );
  }

  get visibleQuickActions(): QuickActionView[] {
    const enabled = new Set(this.prefs?.quickActions || []);
    return this.quickActionOptions.filter((item) => enabled.has(item.command));
  }

  get suggestedAction(): QuickActionView | null {
    if (!this.suggestedCommand) return null;
    return (
      this.quickActionOptions.find(
        (item) => item.command === this.suggestedCommand,
      ) || null
    );
  }

  togglePanel(event?: Event): void {
    event?.stopPropagation();

    if (this.dragged) {
      this.dragged = false;
      return;
    }

    this.open = !this.open;

    if (this.open) {
      if (
        this.activeMeetingAlert?.category === 'REMINDER' &&
        this.activeMeetingAlert.status === 'SCHEDULED'
      ) {
        this.mode = 'agenda';
        this.panelTitle = 'Recordatorio';
        this.panelMessage = '¿Qué quieres hacer con este recordatorio?';
        this.meetings = [this.activeMeetingAlert];
        this.notifications = [];
        this.clearSpeech();
        return;
      }

      this.mode = 'home';
      this.panelTitle = this.prefs.name || 'Asistente ZIVO';
      if (this.suggestedCommand) {
        this.panelMessage = 'Tengo una sugerencia rápida para ti.';
      } else {
        const greeting = this.assistant.renderPhrase('greeting');
        this.panelMessage = greeting?.text || '¿Qué necesitas hacer?';
      }
      this.meetings = [];
      this.notifications = [];
      this.clearSpeech();
      this.setEmotion('happy', 2600);
      this.setTemporaryReaction('wave', 900);
      this.activateTouchMascotAction();
    }
  }

  closePanel(): void {
    this.open = false;
    this.mode = 'home';
  }

  surpriseMe(event?: Event): void {
    event?.stopPropagation();
    this.activateTouchMascotAction();
  }

  submitCommand(): void {
    const raw = this.commandInput.trim();
    if (!raw || this.busy) return;

    const recurring = this.assistant.parseRecurringReminder(raw);
    this.commandInput = '';

    if (recurring) {
      this.startRecurringReminderWizard(recurring);
      return;
    }

    const command = this.assistant.parseCommand(raw);
    this.runCommand(command);
  }

  runSuggestedCommand(): void {
    if (!this.suggestedCommand) return;
    const command = this.suggestedCommand;
    this.suggestedCommand = null;
    this.runCommand(command);
  }

  runCommand(command: AvatarCommand): void {
    this.suggestedCommand = null;

    switch (command) {
      case 'TODAY':
        this.loadAgenda('Hoy', this.assistant.today());
        return;
      case 'TOMORROW':
        this.loadAgenda('Mañana', this.assistant.tomorrow());
        return;
      case 'THIS_WEEK':
        this.loadAgenda('Esta semana', this.assistant.thisWeek());
        return;
      case 'NEXT_MEETING':
        this.loadNextMeeting();
        return;
      case 'MEETINGS':
        this.loadAgenda('Reuniones', this.assistant.meetingsThisWeek());
        return;
      case 'CREATE_REMINDER':
        this.startReminderWizard();
        return;
      case 'LIST_REMINDERS':
        this.loadReminders();
        return;
      case 'NOTIFICATIONS':
        this.loadNotifications();
        return;
      case 'OPEN_CALENDAR':
        this.openCalendar();
        return;
      case 'SETTINGS':
        this.openSettings();
        return;
      case 'MUTE':
        this.assistant.updatePreferences({ muted: true });
        this.mode = 'home';
        this.panelMessage =
          'Quedé en silencio. Puedes volver a activarme desde Configuración.';
        return;
      case 'HIDE':
        this.assistant.updatePreferences({ enabled: false });
        this.closePanel();
        return;
      case 'DANCE':
        this.playMascotCommand('dance', '¡Vamos con un bailecito! 💃');
        return;
      case 'SING':
        this.playMascotCommand('sing', '♪ Mini concierto ZIVO ♫');
        return;
      case 'SLEEP':
        this.playMascotCommand('sleep', 'Modo Zzz por unos segundos 😴');
        return;
      case 'CELEBRATE':
        this.playMascotCommand('celebrate', '¡Eso merece celebración! 🎉');
        return;
      case 'THINK':
        this.playMascotCommand('think', 'Mmm… pensando 🤔');
        return;
      case 'WALK':
        this.playMascotCommand('walk', 'Voy dando una vuelta por aquí 😄');
        return;
      default:
        this.mode = 'home';
        this.panelMessage =
          'Prueba: “hoy”, “mañana”, “esta semana”, “próxima reunión”, “recuérdame”, “mis recordatorios” o “novedades”.';
    }
  }

  openCalendar(): void {
    this.closePanel();
    this.router.navigate(['/meetings']);
  }

  openSettings(): void {
    this.closePanel();
    this.router.navigate(['/configuraciones/avatar-asistente']);
  }

  isTeamsMeeting(meeting: Meeting): boolean {
    return meeting.provider === 'MICROSOFT_TEAMS' && !!meeting.join_url;
  }

  openMeetingLink(meeting: Meeting, event?: Event): void {
    event?.stopPropagation();
    if (!meeting.join_url) return;

    window.open(meeting.join_url, '_blank', 'noopener,noreferrer');
  }

  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;

    this.dragging = true;
    this.roaming = false;
    this.clearTimer(this.roamTimer);
    this.roamTimer = null;
    this.dragged = false;
    this.dragOffset = {
      x: event.clientX - this.position.x,
      y: event.clientY - this.position.y,
    };

    const target = event.currentTarget as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.dragging) return;

    const next = this.clampPosition({
      x: event.clientX - this.dragOffset.x,
      y: event.clientY - this.dragOffset.y,
    });

    if (
      Math.abs(next.x - this.position.x) > 2 ||
      Math.abs(next.y - this.position.y) > 2
    ) {
      this.dragged = true;
    }

    this.position = next;
  }

  @HostListener('document:pointerup')
  onPointerUp(): void {
    if (!this.dragging) return;

    this.dragging = false;
    if (this.dragged) {
      this.assistant.setPosition(this.position);
    }
    this.scheduleRoam(7000);
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (!document.hidden) this.scheduleRoam(5000);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.roaming = false;
    this.position = this.clampPosition(this.position);
    this.scheduleRoam(5000);
  }

  chooseReminderDate(value: string): void {
    if (!value) return;
    this.reminderDraft.date = value;
    this.reminderStep = 2;
    this.reminderError = '';
  }

  chooseToday(): void {
    this.chooseReminderDate(this.dateInputValue(new Date()));
  }

  chooseTomorrow(): void {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    this.chooseReminderDate(this.dateInputValue(date));
  }

  chooseReminderTime(value: string): void {
    const time = String(value || '').trim();
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
      this.reminderError = 'Selecciona una hora válida en formato HH:mm.';
      return;
    }
    const start = new Date(`${this.reminderDraft.date}T${time}:00`);
    if (Number.isNaN(start.getTime()) || start.getTime() < Date.now() - 60000) {
      this.reminderError = 'Esa hora ya pasó. Elige una fecha y hora futuras.';
      return;
    }
    this.reminderDraft.time = time;
    this.reminderStep = 3;
    this.reminderError = '';
  }

  continueReminderContent(): void {
    if (!this.reminderDraft.subject.trim()) {
      this.reminderError = 'Escribe qué quieres recordar.';
      return;
    }

    this.reminderError = '';
    this.reminderStep = 4;
  }

  saveReminder(): void {
    if (this.busy || !this.reminderDraft.subject.trim()) return;
    const start = new Date(`${this.reminderDraft.date}T${this.reminderDraft.time}:00`);
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(this.reminderDraft.time) ||
        Number.isNaN(start.getTime()) || start.getTime() < Date.now() - 60000) {
      this.reminderError = 'Selecciona una fecha y hora futuras válidas.';
      this.reminderStep = 2;
      return;
    }

    this.busy = true;
    this.reaction = 'thinking';
    this.reminderError = '';

    const recurrence = this.buildReminderRecurrence();
    const subscription = this.assistant
      .createReminder({
        ...this.reminderDraft,
        recurrence,
      })
      .subscribe({
        next: (meeting) => {
          this.busy = false;
          this.mode = 'agenda';
          this.panelTitle = 'Recordatorio creado';
          this.panelMessage =
            'Se guardó en ZIVO y se sincronizará con Outlook.';
          this.meetings = [meeting];
          this.reminderStep = 1;
          this.reminderDraft = this.createReminderDraft();
          this.reminderRecurrenceMode = 'none';
          this.reminderRecurrenceDay = null;
          this.scheduleMeetingWatch(0);
          this.react(
            'happy',
            'Listo. Un pendiente menos.',
            'success',
          );
        },
        error: (error) => {
          this.busy = false;
          this.reaction = 'idle';
          this.reminderError =
            error?.error?.message ||
            error?.message ||
            'No se pudo crear el recordatorio.';
        },
      });

    this.subscriptions.add(subscription);
  }

  cancelReminder(): void {
    this.mode = 'home';
    this.reminderStep = 1;
    this.reminderDraft = this.createReminderDraft();
    this.reminderRecurrenceMode = 'none';
    this.reminderRecurrenceDay = null;
    this.reminderError = '';
    this.panelTitle = this.prefs.name || 'Asistente ZIVO';
    this.panelMessage = '¿Qué necesitas hacer?';
  }

  startReminderWizard(): void {
    this.open = true;
    this.mode = 'reminder';
    this.panelTitle = 'Nuevo recordatorio';
    this.panelMessage = 'Lo guardaré en tu calendario de Outlook.';
    this.reminderStep = 1;
    this.reminderDraft = this.createReminderDraft();
    this.reminderRecurrenceMode = 'none';
    this.reminderRecurrenceDay = null;
    this.reminderError = '';
    this.meetings = [];
    this.notifications = [];
  }

  completeReminder(meeting: Meeting, event?: Event): void {
    event?.stopPropagation();
    if (this.reminderActionKey) return;

    this.reminderActionKey = `complete:${meeting.external_id}`;
    const subscription = this.assistant.completeReminder(meeting).subscribe({
      next: () => {
        this.reminderActionKey = '';
        this.meetings = this.meetings.filter(
          (item) => item.external_id !== meeting.external_id,
        );
        if (this.activeMeetingAlert?.external_id === meeting.external_id) {
          this.activeMeetingAlert = null;
        }
        this.panelMessage = 'Recordatorio completado.';
        this.setEmotion('celebrating', 3500);
        this.react('happy', '¡Hecho! Un pendiente menos 🎉', 'success');
        this.scheduleMeetingWatch(0);
      },
      error: () => {
        this.reminderActionKey = '';
        this.panelMessage = 'No pude completar ese recordatorio.';
      },
    });
    this.subscriptions.add(subscription);
  }

  snoozeReminder(
    meeting: Meeting,
    delay: number | 'tomorrow',
    event?: Event,
  ): void {
    event?.stopPropagation();
    if (this.reminderActionKey) return;

    this.reminderActionKey = `snooze:${meeting.external_id}`;
    const subscription = this.assistant.snoozeReminder(meeting, delay).subscribe({
      next: (updated) => {
        this.reminderActionKey = '';
        this.meetings = this.meetings.map((item) =>
          item.external_id === meeting.external_id ? updated : item,
        );
        if (this.activeMeetingAlert?.external_id === meeting.external_id) {
          this.activeMeetingAlert = updated;
        }
        const label =
          delay === 'tomorrow'
            ? 'mañana'
            : delay === 60
              ? '1 hora'
              : `${delay} min`;
        this.panelMessage = `Listo, lo pospuse ${label}.`;
        this.react('happy', `Te lo recordaré en ${label} 👌`, 'reminder');
        this.scheduleMeetingWatch(0);
      },
      error: () => {
        this.reminderActionKey = '';
        this.panelMessage = 'No pude posponer ese recordatorio.';
      },
    });
    this.subscriptions.add(subscription);
  }

  playMiniGame(event?: Event): void {
    event?.stopPropagation();
    if (!this.prefs.mascotStyle.miniGames) return;

    const original = { ...this.position };
    const maxX = Math.max(20, window.innerWidth - 110);
    const maxY = Math.max(80, window.innerHeight - 140);
    this.activeMotionPreset = 'dodge';
    this.setEmotion('happy', 2500);
    this.speech = '¡Casi! 😄 Atrápame otra vez.';
    this.position = {
      x: Math.max(12, Math.min(maxX, original.x + (Math.random() * 260 - 130))),
      y: Math.max(68, Math.min(maxY, original.y + (Math.random() * 180 - 90))),
    };

    this.clearTimer(this.motionTimer);
    this.motionTimer = window.setTimeout(() => {
      this.activeMotionPreset = this.prefs.movementProfile.idle;
      this.speech = '';
    }, 2600);
  }

  formatDateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleString('es-EC', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  trackMeeting(_: number, meeting: Meeting): string {
    return meeting.external_id || `${meeting.subject}-${meeting.start_at}`;
  }

  trackNotification(_: number, notification: Subnotification): number {
    return notification.id;
  }

  trackQuickAction(_: number, action: QuickActionView): AvatarQuickAction {
    return action.command;
  }

  private startRecurringReminderWizard(
    parsed: ParsedRecurringReminder,
  ): void {
    this.open = true;
    this.mode = 'reminder';
    this.panelTitle = 'Recordatorio recurrente';
    this.panelMessage = 'Ya entendí la recurrencia. Elige la hora.';
    this.reminderStep = 2;
    this.reminderDraft = {
      ...this.createReminderDraft(),
      date: parsed.nextDate,
      subject: parsed.subject,
    };
    this.reminderRecurrenceMode = 'weekly';
    this.reminderRecurrenceDay = parsed.dayOfWeek;
    this.reminderError = '';
    this.meetings = [];
    this.notifications = [];
  }

  private buildReminderRecurrence(): MeetingRecurrencePattern | null {
    if (this.reminderRecurrenceMode === 'none') return null;

    const start = new Date(
      `${this.reminderDraft.date || this.dateInputValue(new Date())}T12:00:00`,
    );
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);

    if (this.reminderRecurrenceMode === 'daily') {
      return {
        frequency: 'DAILY',
        interval: 1,
        end_date: this.dateInputValue(end),
      };
    }

    if (this.reminderRecurrenceMode === 'weekly') {
      const day = this.reminderRecurrenceDay || this.weekdayForDate(start);
      return {
        frequency: 'WEEKLY',
        interval: 1,
        end_date: this.dateInputValue(end),
        days_of_week: [day],
      };
    }

    return {
      frequency: 'MONTHLY',
      interval: 1,
      end_date: this.dateInputValue(end),
    };
  }

  private weekdayForDate(
    date: Date,
  ):
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY' {
    const values = [
      'SUNDAY',
      'MONDAY',
      'TUESDAY',
      'WEDNESDAY',
      'THURSDAY',
      'FRIDAY',
      'SATURDAY',
    ] as const;
    return values[date.getDay()];
  }

  private playMascotCommand(
    preset: AvatarMotionPreset,
    message: string,
  ): void {
    this.open = false;
    this.activeMotionPreset = preset;
    this.applyPackStateForMotion(preset);
    this.setEmotion(
      preset === 'celebrate'
        ? 'celebrating'
        : preset === 'sleep'
          ? 'tired'
          : preset === 'think'
            ? 'focused'
            : 'happy',
      3600,
    );

    if (!this.prefs.muted) {
      this.speech = message;
      this.clearTimer(this.speechTimer);
      this.speechTimer = window.setTimeout(() => {
        this.speech = '';
      }, 3600);
    }

    if (preset === 'sing' && this.prefs.movementProfile.singSoundEnabled) {
      this.playMascotJingle();
    }

    this.clearTimer(this.motionTimer);
    this.motionTimer = window.setTimeout(() => {
      this.motionTimer = null;
      this.activeMotionPreset = this.prefs.movementProfile.idle;
      this.applyPackStateForMotion(this.activeMotionPreset);
    }, 3600);
  }

  private notificationVisualState(notification: Subnotification): {
    emotion: AvatarEmotion;
    reaction: AvatarReaction;
    motionContext: 'notification' | 'success';
  } {
    const type = notification.notification?.type;
    const priority = notification.notification?.priority;
    const message = String(notification.notification?.message || '').toLowerCase();

    if (
      type === 'success' ||
      /aprobado|completado|contratado|finalizado|éxito|exito/.test(message)
    ) {
      return {
        emotion: 'celebrating',
        reaction: 'happy',
        motionContext: 'success',
      };
    }

    if (
      type === 'error' ||
      priority === 'high' ||
      /rechazado|vencido|error|urgente|crítico|critico/.test(message)
    ) {
      return {
        emotion: 'worried',
        reaction: 'alert',
        motionContext: 'notification',
      };
    }

    if (type === 'warning') {
      return {
        emotion: 'focused',
        reaction: 'alert',
        motionContext: 'notification',
      };
    }

    return {
      emotion: 'happy',
      reaction: 'wave',
      motionContext: 'notification',
    };
  }

  private setEmotion(emotion: AvatarEmotion, durationMs = 0): void {
    this.emotion = emotion;
    if (!durationMs) return;

    window.setTimeout(() => {
      if (this.emotion === emotion) this.emotion = 'neutral';
    }, durationMs);
  }

  private loadMascotPack(): void {
    this.subscriptions.add(
      this.assistant.animationAssets$.subscribe((assets) => {
        this.packStates.clear();
        assets.forEach((asset) => this.packStates.add(asset.state));
        this.applyPackStateForMotion(this.activeMotionPreset);
      }),
    );

    this.subscriptions.add(
      this.assistant.listAnimationAssets().subscribe({
        error: () => {
          this.packStates.clear();
          this.activePackUrl = null;
        },
      }),
    );
  }

  private applyPackStateForMotion(preset: AvatarMotionPreset): void {
    if (!this.prefs?.mascotStyle.animationPackEnabled) {
      this.activePackUrl = null;
      return;
    }

    const state = this.packStateForMotion(preset);
    if (!this.packStates.has(state)) {
      const fallback: AvatarPackState = 'idle';
      if (!this.packStates.has(fallback)) {
        this.activePackUrl = null;
        return;
      }
      this.loadPackState(fallback);
      return;
    }

    this.loadPackState(state);
  }

  private packStateForMotion(preset: AvatarMotionPreset): AvatarPackState {
    if (preset === 'dance') return 'dance';
    if (preset === 'sing') return 'sing';
    if (preset === 'sleep') return 'sleep';
    if (preset === 'think') return 'think';
    if (preset === 'celebrate') return 'celebrate';
    if (preset === 'walk' || preset === 'dodge') return 'walk';
    if (preset === 'shake' || preset === 'wave') return 'alert';
    return 'idle';
  }

  private loadPackState(state: AvatarPackState): void {
    const subscription = this.assistant.getAnimationUrl(state).subscribe({
      next: (url) => {
        this.activePackUrl = url;
      },
    });
    this.subscriptions.add(subscription);
  }

  private scheduleDailyBriefs(delayMs = 0): void {
    this.clearTimer(this.dailyBriefTimer);
    this.dailyBriefTimer = window.setTimeout(() => {
      this.runDailyBriefs();
      this.dailyBriefTimer = window.setTimeout(
        () => this.scheduleDailyBriefs(0),
        30 * 60 * 1000,
      );
    }, Math.max(0, delayMs));
  }

  private runDailyBriefs(): void {
    if (
      this.briefCheckRunning ||
      !this.prefs?.enabled ||
      this.prefs.muted
    ) {
      return;
    }

    const hour = new Date().getHours();
    const closing = hour >= 17;

    if (
      closing &&
      this.assistant.shouldShowDailyBrief('closing')
    ) {
      this.briefCheckRunning = true;
      const subscription = this.assistant.closingSummary().subscribe({
        next: (summary) => {
          this.briefCheckRunning = false;
          this.assistant.markDailyBriefShown('closing');
          this.setEmotion('focused', 5000);
          const name = this.assistant.getCurrentUserFirstName();
          this.speech =
            `${name ? name + ', ' : ''}para mañana tienes ${summary.meetings} reunión${summary.meetings === 1 ? '' : 'es'}, ` +
            `${summary.reminders} recordatorio${summary.reminders === 1 ? '' : 's'} y ` +
            `${summary.notifications} novedad${summary.notifications === 1 ? '' : 'es'} pendiente${summary.notifications === 1 ? '' : 's'}.`;
        },
        error: () => {
          this.briefCheckRunning = false;
        },
      });
      this.subscriptions.add(subscription);
      return;
    }

    if (
      hour < 17 &&
      this.assistant.shouldShowDailyBrief('startup')
    ) {
      this.briefCheckRunning = true;
      const subscription = this.assistant.dailySummary().subscribe({
        next: (summary) => {
          this.briefCheckRunning = false;
          this.assistant.markDailyBriefShown('startup');
          this.setEmotion('happy', 5000);
          const name = this.assistant.getCurrentUserFirstName();
          this.speech =
            `${name ? name + ', ' : ''}hoy tienes ${summary.meetings} reunión${summary.meetings === 1 ? '' : 'es'}, ` +
            `${summary.reminders} recordatorio${summary.reminders === 1 ? '' : 's'} y ` +
            `${summary.notifications} novedad${summary.notifications === 1 ? '' : 'es'}.`;
        },
        error: () => {
          this.briefCheckRunning = false;
        },
      });
      this.subscriptions.add(subscription);
    }
  }

  private loadAgenda(
    title: string,
    source: Observable<Meeting[]>,
  ): void {
    this.prepareLoading(title);

    const subscription = source.subscribe({
      next: (meetings) => {
        this.busy = false;
        this.reaction = 'idle';
        this.mode = 'agenda';
        this.meetings = meetings;
        this.panelMessage = meetings.length
          ? `Tienes ${meetings.length} compromiso${meetings.length === 1 ? '' : 's'}.`
          : 'No tienes compromisos registrados para este periodo.';
      },
      error: () => {
        this.handleLoadError('No pude consultar tu agenda.');
      },
    });

    this.subscriptions.add(subscription);
  }

  private loadNextMeeting(): void {
    this.prepareLoading('Próxima reunión');

    const subscription = this.assistant.nextMeeting().subscribe({
      next: (meeting) => {
        this.busy = false;
        this.reaction = 'idle';
        this.mode = 'agenda';
        this.meetings = meeting ? [meeting] : [];
        this.panelMessage = meeting
          ? 'Este es tu siguiente compromiso.'
          : 'No encontré reuniones próximas.';
      },
      error: () => {
        this.handleLoadError('No pude consultar tu próxima reunión.');
      },
    });

    this.subscriptions.add(subscription);
  }

  private loadReminders(): void {
    this.prepareLoading('Recordatorios y notas');

    const subscription = this.assistant.listReminders().subscribe({
      next: (meetings) => {
        this.busy = false;
        this.reaction = 'idle';
        this.mode = 'agenda';
        this.meetings = meetings;
        this.panelMessage = meetings.length
          ? `Tienes ${meetings.length} recordatorio${meetings.length === 1 ? '' : 's'} pendiente${meetings.length === 1 ? '' : 's'} o reciente${meetings.length === 1 ? '' : 's'}.`
          : 'No tienes recordatorios pendientes ni recientes.';
      },
      error: () => {
        this.handleLoadError('No pude consultar tus recordatorios.');
      },
    });

    this.subscriptions.add(subscription);
  }

  private loadNotifications(): void {
    this.prepareLoading('Novedades');

    const subscription = this.assistant.unreadNotifications().subscribe({
      next: (notifications) => {
        this.busy = false;
        this.reaction = 'idle';
        this.mode = 'notifications';
        this.notifications = notifications;
        this.panelMessage = notifications.length
          ? `Tienes ${notifications.length} novedad${notifications.length === 1 ? '' : 'es'} sin leer.`
          : 'No tienes novedades pendientes.';
      },
      error: () => {
        this.handleLoadError('No pude consultar tus novedades.');
      },
    });

    this.subscriptions.add(subscription);
  }

  private prepareLoading(title: string): void {
    this.open = true;
    this.busy = true;
    this.mode = 'agenda';
    this.panelTitle = title;
    this.panelMessage = 'Consultando…';
    this.meetings = [];
    this.notifications = [];
    this.reaction = 'thinking';
  }

  private handleLoadError(message: string): void {
    this.busy = false;
    this.reaction = 'idle';
    this.mode = 'home';
    this.panelMessage = message;
  }

  private createReminderDraft(): ReminderDraft {
    return {
      date: '',
      time: this.nextSuggestedTime(),
      subject: '',
      description: '',
      reminderMinutes: 15,
    };
  }

  private nextSuggestedTime(): string {
    const next = new Date(Date.now() + 30 * 60 * 1000);
    next.setMinutes(Math.ceil(next.getMinutes() / 15) * 15, 0, 0);
    return `${String(next.getHours()).padStart(2, '0')}:${String(next.getMinutes()).padStart(2, '0')}`;
  }

  private handleNotificationStream(rows: Subnotification[]): void {
    const currentIds = new Set(rows.map((row) => row.id));

    if (!this.notificationInitialized) {
      this.notificationInitialized = true;
      this.knownNotificationIds = currentIds;
      return;
    }

    const fresh = rows.find((row) => !this.knownNotificationIds.has(row.id));
    this.knownNotificationIds = currentIds;

    if (
      fresh &&
      this.prefs.warningEnabled &&
      !this.prefs.muted &&
      this.prefs.enabled
    ) {
      const rendered = this.assistant.renderPhrase('notification', {
        message: fresh.notification?.message || '',
      });
      const message =
        rendered?.text ||
        fresh.notification?.message ||
        this.assistant.getWarningPhrase();

      this.suggestedCommand = 'NOTIFICATIONS';

      const eventState = this.notificationVisualState(fresh);
      this.setEmotion(eventState.emotion, 4200);
      this.react(
        eventState.reaction,
        message,
        eventState.motionContext,
      );
      void this.assistant.playSound('notification');
    }
  }

  private scheduleMeetingWatch(delayMs = 0): void {
    this.clearTimer(this.meetingWatchTimer);
    this.meetingWatchTimer = null;

    if (!this.prefs.enabled || !this.prefs.meetingAlertsEnabled) {
      return;
    }

    this.meetingWatchTimer = window.setTimeout(() => {
      this.meetingWatchTimer = null;
      this.checkMeetingAlerts();
    }, Math.max(0, delayMs));
  }

  private checkMeetingAlerts(): void {
    if (this.meetingCheckRunning) {
      this.scheduleMeetingWatch(30 * 1000);
      return;
    }

    this.meetingCheckRunning = true;

    const subscription = this.assistant.upcomingForAlerts(12).subscribe({
      next: (meetings) => {
        this.meetingCheckRunning = false;
        const now = Date.now();
        this.handleMicrosoftMeetingArrivals(meetings, now);
        const orderedMeetings = [...meetings].sort((left, right) => {
          const leftStart = new Date(left.start_at).getTime();
          const rightStart = new Date(right.start_at).getTime();
          const leftOverdue =
            left.category === 'REMINDER' && leftStart < now;
          const rightOverdue =
            right.category === 'REMINDER' && rightStart < now;

          if (leftOverdue !== rightOverdue) {
            return leftOverdue ? 1 : -1;
          }

          return leftOverdue
            ? rightStart - leftStart
            : leftStart - rightStart;
        });

        const candidate = orderedMeetings.find((meeting) => {
          if (meeting.is_all_day) return false;

          const startsAt = new Date(meeting.start_at).getTime();
          const isReminder = meeting.category === 'REMINDER';
          const leadMinutes = isReminder
            ? Math.max(0, Number(meeting.reminder_minutes || 0))
            : Math.max(1, Number(this.prefs.meetingAlertMinutes || 10));
          const triggerAt = startsAt - leadMinutes * 60 * 1000;
          const graceMs = isReminder ? 24 * 60 * 60 * 1000 : 0;

          if (now < triggerAt || now > startsAt + graceMs) return false;

          const key = `${meeting.external_id || meeting.id}-${meeting.start_at}-${leadMinutes}`;
          return (
            !this.alertedMeetingKeys.has(key) &&
            !this.assistant.wasAlertedEvent(key)
          );
        });

        if (!candidate) {
          this.scheduleMeetingWatch(
            this.nextMeetingWatchDelay(meetings, now),
          );
          return;
        }

        const isReminder = candidate.category === 'REMINDER';
        const leadMinutes = isReminder
          ? Math.max(0, Number(candidate.reminder_minutes || 0))
          : Math.max(1, Number(this.prefs.meetingAlertMinutes || 10));
        const key = `${candidate.external_id || candidate.id}-${candidate.start_at}-${leadMinutes}`;
        this.alertedMeetingKeys.add(key);
        this.assistant.markAlertedEvent(key);
        this.activeMeetingAlert = candidate;

        const startsAt = new Date(candidate.start_at).getTime();
        const minutes = Math.max(
          0,
          Math.ceil((startsAt - Date.now()) / (60 * 1000)),
        );
        const overdue = isReminder && Date.now() > startsAt;
        const overdueMinutes = overdue
          ? Math.max(1, Math.floor((Date.now() - startsAt) / (60 * 1000)))
          : 0;
        const category = overdue
          ? 'overdue'
          : isReminder
            ? 'reminder'
            : 'meeting';
        const rendered = this.assistant.renderPhrase(category, {
          title: candidate.subject,
          minutes,
          time: this.formatTime(candidate.start_at),
          delay: overdue ? this.formatDelay(overdueMinutes) : '',
          platform:
            candidate.provider === 'MICROSOFT_TEAMS'
              ? 'Teams'
              : candidate.provider === 'MICROSOFT_OUTLOOK'
                ? 'Outlook'
                : 'tu reunión',
        });

        this.suggestedCommand = isReminder
          ? 'LIST_REMINDERS'
          : 'NEXT_MEETING';

        this.react(
          (rendered?.reaction as AvatarReaction) ||
            (isReminder ? 'alert' : 'wave'),
          rendered?.text ||
            (overdue
              ? `Se te pasó “${candidate.subject}” hace ${this.formatDelay(overdueMinutes)}.`
              : isReminder
                ? `Tienes “${candidate.subject}” pendiente.`
                : `“${candidate.subject}” empieza en ${minutes} min.`),
          isReminder ? 'reminder' : 'meeting',
        );

        void this.assistant.playSound(isReminder ? 'reminder' : 'meeting');

        this.scheduleMeetingWatch(
          overdue
            ? 4 * 60 * 1000
            : this.nextMeetingWatchDelay(meetings, Date.now()),
        );
      },
      error: () => {
        this.meetingCheckRunning = false;
        this.scheduleMeetingWatch(4 * 60 * 1000);
      },
    });

    this.subscriptions.add(subscription);
  }

  private nextMeetingWatchDelay(
    meetings: Meeting[],
    now = Date.now(),
  ): number {
    const minute = 60 * 1000;
    const defaultDelay = 2 * minute;

    let nearestTriggerDelta = Number.POSITIVE_INFINITY;

    for (const meeting of meetings) {
      if (meeting.is_all_day) continue;

      const startsAt = new Date(meeting.start_at).getTime();
      if (!Number.isFinite(startsAt)) continue;

      const isReminder = meeting.category === 'REMINDER';
      const leadMinutes = isReminder
        ? Math.max(0, Number(meeting.reminder_minutes || 0))
        : Math.max(1, Number(this.prefs.meetingAlertMinutes || 10));
      const key = `${meeting.external_id || meeting.id}-${meeting.start_at}-${leadMinutes}`;

      if (
        this.alertedMeetingKeys.has(key) ||
        this.assistant.wasAlertedEvent(key)
      ) {
        continue;
      }

      const triggerAt = startsAt - leadMinutes * minute;
      const graceMs = isReminder ? 24 * 60 * minute : 0;

      // Si el momento útil de alerta ya pasó, no mantiene el polling rápido.
      if (now > startsAt + graceMs) continue;

      const delta = triggerAt - now;

      if (delta < nearestTriggerDelta) {
        nearestTriggerDelta = delta;
      }
    }

    if (!Number.isFinite(nearestTriggerDelta)) {
      return defaultDelay;
    }

    if (nearestTriggerDelta <= 5 * minute) {
      return 30 * 1000;
    }

    if (nearestTriggerDelta <= 30 * minute) {
      return 60 * 1000;
    }

    return defaultDelay;
  }

  /** Announce newly synchronized Microsoft calendar invitations before they start. */
  private handleMicrosoftMeetingArrivals(meetings: Meeting[], now: number): void {
    const microsoft = meetings.filter((meeting) =>
      (meeting.provider === 'MICROSOFT_TEAMS' || meeting.provider === 'MICROSOFT_OUTLOOK') &&
      meeting.status === 'SCHEDULED' && new Date(meeting.start_at).getTime() > now,
    );
    const keys = new Set(microsoft.map((meeting) => String(meeting.external_id || meeting.id)));
    if (!this.microsoftMeetingInitialized) {
      this.microsoftMeetingInitialized = true;
      this.knownMicrosoftMeetingKeys = keys;
      return;
    }
    const fresh = microsoft.find((meeting) =>
      !this.knownMicrosoftMeetingKeys.has(String(meeting.external_id || meeting.id)),
    );
    this.knownMicrosoftMeetingKeys = keys;
    if (!fresh || !this.prefs.warningEnabled || !this.prefs.enabled) return;
    const source = fresh.provider === 'MICROSOFT_TEAMS' ? 'Teams' : 'Outlook';
    const message = `📩 Nueva reunión en ${source}: ${fresh.subject || 'sin título'}. ¿La revisamos?`;
    this.suggestedCommand = 'NEXT_MEETING';
    this.setEmotion('focused', 4200);
    if (this.open && this.mode === 'home') this.panelMessage = message;
    this.react('wave', message, 'notification');
    void this.assistant.playSound('notification');
  }

  /** Wander within the viewport; never interrupt a form, manual drag or reduced-motion preference. */
  private scheduleRoam(delayMs = 9000): void {
    this.clearTimer(this.roamTimer);
    this.roamTimer = null;
    if (!this.prefs?.enabled || this.prefs.movement === 'quiet' ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.roaming = false;
      return;
    }
    this.roamTimer = window.setTimeout(() => {
      this.roamTimer = null;
      this.roamAcrossWindow();
      this.scheduleRoam(9500 + Math.random() * 8500);
    }, delayMs);
  }

  private roamAcrossWindow(): void {
    if (document.hidden || this.open || this.dragging || !this.prefs.enabled) return;
    const reach = this.prefs.movement === 'active' ? 185 : 110;
    const direction = Math.random() < 0.5 ? -1 : 1;
    const next = this.clampPosition({
      x: this.position.x + direction * (50 + Math.random() * reach),
      y: this.position.y + (Math.random() - 0.5) * reach * 1.2,
    });
    if (Math.abs(next.x - this.position.x) < 8 && Math.abs(next.y - this.position.y) < 8) return;
    this.roaming = true;
    this.position = next;
    if (this.motionTimer !== null) return;
    const movements: AvatarMotionPreset[] = ['walk', 'jump', 'think', 'spin', 'dance', 'shake'];
    const preset = movements[Math.floor(Math.random() * movements.length)];
    this.activeMotionPreset = preset;
    this.applyPackStateForMotion(preset);
    if (preset === 'think') this.setEmotion('focused', 2800);
    else if (preset === 'shake') this.setEmotion('worried', 2800);
    else this.setEmotion('happy', 2800);
    if (this.prefs.randomInteractionsEnabled && !this.prefs.muted && !this.speech && Math.random() < 0.12) {
      const phrases: Record<string, string> = {
        think: 'Estoy pensando… ¿qué sigue? 🤔',
        shake: '¡Ánimo, esos pendientes no van a ganarnos! 😤',
        spin: '¡Una voltereta y seguimos! 🤸',
        walk: '¿Revisamos tu agenda?',
        jump: '¡Buen trabajo! Sigue así 🚀',
        dance: '¡Un paso a la vez! 😄',
      };
      this.speech = phrases[preset] || '¡Seguimos!';
      this.clearTimer(this.speechTimer);
      this.speechTimer = window.setTimeout(() => { this.speech = ''; }, 4200);
    }
    this.motionTimer = window.setTimeout(() => {
      this.motionTimer = null;
      this.activeMotionPreset = this.prefs.movementProfile.idle;
      this.applyPackStateForMotion(this.activeMotionPreset);
    }, 2700);
  }

  private scheduleRandomInteraction(): void {
    this.clearTimer(this.randomInteractionTimer);
    this.randomInteractionTimer = null;

    if (
      !this.prefs?.enabled ||
      !this.prefs.randomInteractionsEnabled ||
      this.prefs.muted
    ) {
      return;
    }

    const minMinutes = Math.max(
      2,
      Number(this.prefs.randomInteractionMinMinutes || 4),
    );
    const maxMinutes = Math.max(
      minMinutes,
      Number(this.prefs.randomInteractionMaxMinutes || 10),
    );
    const delayMinutes =
      minMinutes + Math.random() * (maxMinutes - minMinutes);

    this.randomInteractionTimer = window.setTimeout(() => {
      this.emitRandomInteraction();
      this.scheduleRandomInteraction();
    }, delayMinutes * 60 * 1000);
  }

  private emitRandomInteraction(): void {
    if (
      this.open ||
      this.speech ||
      this.dragging ||
      !this.prefs.enabled ||
      this.prefs.muted
    ) {
      return;
    }

    const useMotivation =
      this.prefs.motivationalEnabled && Math.random() < 0.68;

    if (useMotivation) {
      this.suggestedCommand = null;
      const phrase = this.assistant.renderCasualPhrase();

      this.react(
        (phrase?.reaction as AvatarReaction) || 'happy',
        phrase?.text || this.assistant.getMotivationalPhrase(),
        'motivation',
      );
      return;
    }

    const actions = this.prefs.quickActions || [];
    if (!actions.length) return;

    const command = actions[Math.floor(Math.random() * actions.length)];
    const suggestion = this.suggestionFor(command);
    this.suggestedCommand = command;
    this.react('wave', suggestion, 'motivation');
  }

  private suggestionFor(command: AvatarQuickAction): string {
    const messages: Record<AvatarQuickAction, string> = {
      TODAY: '¿Revisamos qué tienes para hoy?',
      TOMORROW: 'Puedo mostrarte lo que tienes mañana.',
      THIS_WEEK: '¿Quieres revisar tu semana?',
      NEXT_MEETING: 'Puedo buscar tu próxima reunión.',
      CREATE_REMINDER: 'Si necesitas recordar algo, tócame y lo agendamos.',
      LIST_REMINDERS: 'Puedo mostrarte tus notas y recordatorios.',
      NOTIFICATIONS: '¿Revisamos tus novedades pendientes?',
      OPEN_CALENDAR: 'Tu calendario está a un toque.',
    };
    return messages[command];
  }

  private react(
    reaction: AvatarReaction,
    text: string,
    motionContext?:
      | 'notification'
      | 'meeting'
      | 'reminder'
      | 'motivation'
      | 'greeting'
      | 'touch'
      | 'success',
  ): void {
    this.reaction = reaction;

    if (motionContext) {
      this.activateMotion(motionContext, 3200);
    }

    if (!this.prefs.muted) {
      this.speech = text;
      this.clearTimer(this.speechTimer);
      this.speechTimer = window.setTimeout(() => {
        this.speech = '';
      }, 7000);
    }

    this.setTemporaryReaction(reaction, 3200);
  }

  private activateMotion(
    context:
      | 'notification'
      | 'meeting'
      | 'reminder'
      | 'motivation'
      | 'greeting'
      | 'touch'
      | 'success',
    durationMs: number,
    explicitUserAction = false,
  ): void {
    this.clearTimer(this.motionTimer);

    let preset = this.prefs.movementProfile[context];
    if (context === 'touch' && this.prefs.movementProfile.touchRandom) {
      preset = this.randomFunMotion();
    }

    this.activeMotionPreset = preset;
    this.applyPackStateForMotion(preset);

    if (
      explicitUserAction &&
      preset === 'sing' &&
      this.prefs.movementProfile.singSoundEnabled
    ) {
      this.playMascotJingle();
    }

    this.motionTimer = window.setTimeout(() => {
      this.motionTimer = null;
      this.activeMotionPreset = this.prefs.movementProfile.idle;
      this.applyPackStateForMotion(this.activeMotionPreset);
    }, durationMs);
  }

  private activateTouchMascotAction(): void {
    this.activateMotion('touch', 3000, true);

    if (!this.prefs.muted) {
      const messages: Partial<Record<AvatarMotionPreset, string>> = {
        dance: '¡Un bailecito y seguimos! 😄',
        sing: '♪ Mini concierto ZIVO ♫',
        celebrate: '¡Vamos! 🎉',
        sleep: 'Cinco segunditos de modo Zzz 😴',
        think: 'Mmm… pensando 🤔',
        jump: '¡Hop! 😄',
        wave: '¡Hola! 👋',
        spin: '¡Vuelta completa! 🌀',
      };
      const configuredPhrase =
        this.assistant.renderPhrase('custom') ||
        this.assistant.renderCasualPhrase();
      const message =
        configuredPhrase?.text || messages[this.activeMotionPreset];
      if (message) {
        this.speech = message;
        this.clearTimer(this.speechTimer);
        this.speechTimer = window.setTimeout(() => {
          this.speech = '';
        }, 3200);
      }
    }
  }

  private randomFunMotion(): AvatarMotionPreset {
    const motions: AvatarMotionPreset[] = [
      'dance',
      'sing',
      'celebrate',
      'jump',
      'wave',
      'spin',
      'wobble',
      'dodge',
    ];
    return motions[Math.floor(Math.random() * motions.length)];
  }

  private playMascotJingle(): void {
    if (
      !this.prefs.soundEnabled ||
      this.prefs.muted ||
      !this.prefs.movementProfile.singSoundEnabled
    ) {
      return;
    }

    const AudioContextCtor =
      window.AudioContext ||
      (window as typeof window & {
        webkitAudioContext?: typeof AudioContext;
      }).webkitAudioContext;

    if (!AudioContextCtor) return;

    try {
      const context = new AudioContextCtor();
      const gain = context.createGain();
      const volume = Math.max(
        0.02,
        Math.min(0.18, (this.prefs.soundVolume / 100) * 0.16),
      );
      gain.gain.setValueAtTime(volume, context.currentTime);
      gain.connect(context.destination);

      const notes = [523.25, 659.25, 783.99, 659.25];
      notes.forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const noteGain = context.createGain();
        const start = context.currentTime + index * 0.16;
        const end = start + 0.14;

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, start);
        noteGain.gain.setValueAtTime(0.0001, start);
        noteGain.gain.exponentialRampToValueAtTime(1, start + 0.02);
        noteGain.gain.exponentialRampToValueAtTime(0.0001, end);

        oscillator.connect(noteGain);
        noteGain.connect(gain);
        oscillator.start(start);
        oscillator.stop(end);
      });

      window.setTimeout(() => {
        void context.close();
      }, 1200);
    } catch {
      // El navegador puede bloquear WebAudio hasta una interacción válida.
    }
  }

  private setTemporaryReaction(
    reaction: AvatarReaction,
    durationMs: number,
  ): void {
    this.reaction = reaction;
    this.clearTimer(this.reactionTimer);
    this.reactionTimer = window.setTimeout(() => {
      this.reaction = 'idle';
    }, durationMs);
  }

  private clearSpeech(): void {
    this.speech = '';
    this.clearTimer(this.speechTimer);
    this.speechTimer = null;
  }

  private clearTimer(timer: number | null): void {
    if (timer !== null) {
      window.clearTimeout(timer);
    }
  }

  private initialPosition(preferences: AvatarPreferences): {
    x: number;
    y: number;
  } {
    if (preferences.position) {
      return this.clampPosition(preferences.position);
    }

    return this.clampPosition({
      x: 24,
      y: window.innerHeight - 145,
    });
  }

  private clampPosition(position: { x: number; y: number }): {
    x: number;
    y: number;
  } {
    const margin = 10;
    const avatarSize =
      this.prefs?.size === 'large'
        ? 86
        : this.prefs?.size === 'small'
          ? 58
          : 70;

    return {
      x: Math.max(
        margin,
        Math.min(position.x, window.innerWidth - avatarSize - margin),
      ),
      y: Math.max(
        68,
        Math.min(position.y, window.innerHeight - avatarSize - margin),
      ),
    };
  }

  private formatTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('es-EC', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private formatDelay(totalMinutes: number): string {
    const minutes = Math.max(1, Math.floor(totalMinutes));
    if (minutes < 60) return `${minutes} min`;

    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    if (!remaining) {
      return `${hours} h`;
    }

    return `${hours} h ${remaining} min`;
  }

  private dateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
